/**
 * 数字格式化工具
 */

import { EMPTY_PLACEHOLDER } from './constants';

/**
 * 格式化数字 (千分位分隔)
 *
 * 使用 `Intl.NumberFormat` 实现，支持多语言 locale、性能更优。
 *
 * @param num - 数字
 * @param precision - 小数位数，默认不处理
 * @param locale - BCP 47 语言标签，如 'zh-CN'、'en-US'，默认使用浏览器 locale
 * @returns 格式化后的字符串
 */
export function formatNumber(
  num: number | string | null | undefined,
  precision?: number,
  locale?: string,
): string {
  if (num === null || num === undefined || num === '') return EMPTY_PLACEHOLDER;
  const n = typeof num === 'string' ? Number.parseFloat(num) : num;
  if (Number.isNaN(n)) return EMPTY_PLACEHOLDER;

  const options: Intl.NumberFormatOptions = {};
  if (precision !== undefined) {
    options.minimumFractionDigits = precision;
    options.maximumFractionDigits = precision;
  }

  return new Intl.NumberFormat(locale, options).format(n);
}

/**
 * 格式化货币
 *
 * 使用 `Intl.NumberFormat` 实现，支持多语言 locale。
 * 默认使用前缀符号模式（`¥1,234.50`），与原有行为一致。
 *
 * @param amount - 金额
 * @param currency - 货币符号，默认 '¥'
 * @param precision - 小数位数，默认 2
 * @param locale - BCP 47 语言标签，默认使用浏览器 locale
 * @returns 格式化后的货币字符串
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string = '¥',
  precision: number = 2,
  locale?: string,
): string {
  if (amount === null || amount === undefined || amount === '')
    return EMPTY_PLACEHOLDER;
  const n = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  if (Number.isNaN(n)) return EMPTY_PLACEHOLDER;
  return `${currency}${formatNumber(n, precision, locale)}`;
}

/**
 * 格式化百分比
 *
 * @param value - 数值 (0-1 或 0-100)
 * @param decimals - 小数位数，默认 2
 * @param isRatio - 是否为比率 (0-1)，默认 false
 * @returns 格式化后的百分比字符串
 */
export function formatPercentage(
  value: number | null | undefined,
  decimals: number = 2,
  isRatio: boolean = false,
): string {
  if (value === null || value === undefined) return EMPTY_PLACEHOLDER;
  const pct = isRatio ? value * 100 : value;
  return `${pct.toFixed(decimals)}%`;
}

/**
 * 格式化文件大小
 *
 * @param bytes - 字节数
 * @param decimals - 小数位数，默认 2
 * @returns 格式化后的文件大小字符串
 */
export function formatBytes(
  bytes: number | null | undefined,
  decimals: number = 2,
): string {
  if (bytes === null || bytes === undefined) return EMPTY_PLACEHOLDER;
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const size = bytes / k ** i;

  return `${size.toFixed(decimals)} ${sizes[i]}`;
}
